import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { useDevices } from "../devices";
import { Wifi, WifiOff, Gauge, Radio, Copy, CheckCircle2, Bell, Car, LinkIcon } from "lucide-react";
import SOSButton from "../components/SOSButton";

/**
 * Simulated Car Head Unit screen.
 * Polls the backend for this device's latest location and shows status.
 * In a real Android head-unit app, this screen would be shown on the car display
 * while a background service POSTs GPS to /api/location.
 *
 * IMPORTANT: Uses the user's currently selected (linked) device ONLY.
 * No fallback/demo device — prevents 403 errors and wrong-device data.
 */

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function HeadUnit() {
  const { selectedDeviceId, selectedDevice } = useDevices();
  const [live, setLive] = useState(null);
  const [connected, setConnected] = useState(true);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const DEVICE_ID = selectedDeviceId;
  const deviceName = selectedDevice?.name || "My Car";

  // Poll health
  useEffect(() => {
    const poll = async () => {
      try {
        await api.get("/health");
        setConnected(true);
      } catch {
        setConnected(false);
      }
    };
    poll();
    timerRef.current = setInterval(poll, 3000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Fetch live device stats
  useEffect(() => {
    if (!DEVICE_ID) return;
    const fetchLive = async () => {
      try {
        const token = localStorage.getItem("ct_token");
        if (!token) return;
        const { data } = await api.get(`/location/live/${DEVICE_ID}`);
        setLive(data);
      } catch (_) {}
    };
    fetchLive();
    const iv = setInterval(fetchLive, 5000);
    return () => clearInterval(iv);
  }, [DEVICE_ID]);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(DEVICE_ID);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  const speed = Math.round(live?.last_location?.speed || 0);
  const signal = live?.last_location?.signal ?? 4;

  // ── No device linked → show CTA instead of making bogus API calls ──
  if (!selectedDeviceId) {
    return (
      <div
        data-testid="headunit-no-device"
        className="h-[100vh] h-[100dvh] w-full flex flex-col items-center justify-center bg-slate-950 text-white px-6 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-6">
          <Car size={36} className="text-slate-400" />
        </div>
        <h1 className="text-2xl font-black">No Device Linked</h1>
        <p className="mt-2 text-slate-400 text-sm max-w-xs">
          Link your car's tracker device first to see live data on this screen.
        </p>
        <Link
          to="/link"
          data-testid="headunit-link-cta"
          className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-white font-bold text-sm"
        >
          <LinkIcon size={16} /> Link Device
        </Link>
        <Link
          to="/home"
          className="mt-3 text-primary text-sm font-bold hover:underline"
          data-testid="headunit-to-home"
        >
          ← Back to Home
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="headunit-screen"
      className="h-[100vh] h-[100dvh] w-full flex flex-col bg-slate-950 text-white overflow-hidden"
    >
      {/* top bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Car size={18} />
          </div>
          <div>
            <div className="text-sm font-black">{deviceName}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">
              {connected ? "Service running" : "Reconnecting…"}
            </div>
          </div>
        </div>
        <div
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
            connected ? "bg-moving/20 text-moving" : "bg-parked/20 text-parked"
          }`}
          data-testid="headunit-conn"
        >
          {connected ? <Wifi size={14} /> : <WifiOff size={14} />}
          {connected ? "Connected" : "Disconnected"}
        </div>
      </div>

      {/* Giant speedo */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 flex items-center gap-1">
          <Gauge size={12} /> Current speed
        </div>
        <div className="flex items-end gap-3 mt-1">
          <span data-testid="headunit-speed" className="text-[140px] leading-none font-black tracking-tight">
            {speed}
          </span>
          <span className="text-2xl font-bold text-slate-400 mb-6">km/h</span>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <Radio size={14} className="text-primary" />
          <span className="text-xs uppercase tracking-wider text-slate-400">GPS signal</span>
          <div className="flex items-end gap-0.5 ml-1" data-testid="headunit-signal">
            {[1, 2, 3, 4].map((b) => (
              <span
                key={b}
                className={`w-1.5 rounded-sm ${b <= signal ? "bg-primary" : "bg-white/20"}`}
                style={{ height: `${b * 4 + 4}px` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="px-6 py-5 border-t border-white/10 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Device ID</div>
          <div className="font-mono font-bold text-lg tracking-wider" data-testid="headunit-device-id">
            {DEVICE_ID}
          </div>
          <div className="text-[10px] text-slate-500">
            Last ping {timeAgo(live?.last_location?.timestamp)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SOSButton deviceId={DEVICE_ID} variant="headunit" />
          <button
            data-testid="headunit-copy"
            onClick={copyId}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold"
          >
            {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            {copied ? "Copied!" : "Copy ID"}
          </button>
        </div>
      </div>

      {/* Persistent notification */}
      <div className="bg-primary/10 border-t border-primary/30 px-6 py-2.5 flex items-center gap-2 text-xs">
        <Bell size={14} className="text-primary" />
        <span className="text-slate-200 font-semibold">CarTrack is running</span>
        <Link
          to="/home"
          className="ml-auto text-primary font-bold hover:underline"
          data-testid="headunit-to-home"
        >
          Owner App →
        </Link>
      </div>
    </div>
  );
}
